import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogClose } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import api, { adminApi } from '../../lib/api';
import { Settings, Save, Loader2, Bell, Mail, Send, Users, UserCheck, Clock, Pause, Play, Trash2, CalendarClock, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const AdminSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Notification controls state
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [bulkEmailSetting, setBulkEmailSetting] = useState(true);
  const [updatingBulk, setUpdatingBulk] = useState(false);
  const [sendingAdhoc, setSendingAdhoc] = useState(false);
  const [adhocDialogOpen, setAdhocDialogOpen] = useState(false);

  // Scheduled digest state
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: 'Daily Digest',
    user_ids: [],
    group_ids: [],
    send_hour: 8,
    send_minute: 0,
    exclude_weekends: true,
    active: true
  });
  const [creatingSchedule, setCreatingSchedule] = useState(false);

  const defaultSettings = useMemo(() => ({
    app_name: 'CORE',
    maintenance_mode: false,
    allow_registration: true,
    max_tasks_free: 100,
    max_tasks_premium: 10000,
    ai_enabled: true,
    focus_timer_default: 25
  }), []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await adminApi.getSettings();
      setSettings({ ...defaultSettings, ...response.data.settings });
    } catch (error) {
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  }, [defaultSettings]);

  const fetchSchedules = useCallback(async () => {
    setLoadingSchedules(true);
    try {
      const res = await api.get('/admin/digest/schedules');
      setSchedules(res.data.schedules || []);
    } catch (error) {
      console.error('Failed to fetch schedules');
    } finally {
      setLoadingSchedules(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchSchedules();
  }, [fetchSettings, fetchSchedules]);

  // Fetch users and groups for notification controls
  useEffect(() => {
    const fetchUsersAndGroups = async () => {
      setLoadingUsers(true);
      try {
        const [usersRes, groupsRes] = await Promise.all([
          adminApi.getUsers({ limit: 500 }),
          api.get('/admin/groups')
        ]);
        setUsers(usersRes.data.users || []);
        setGroups(groupsRes.data.groups || []);
      } catch (error) {
        console.error('Failed to fetch users/groups');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsersAndGroups();
  }, []);

  const handleBulkEmailUpdate = async () => {
    if (selectedUserIds.length === 0 && selectedGroupIds.length === 0) {
      toast.error('Please select at least one user or group');
      return;
    }
    setUpdatingBulk(true);
    try {
      const response = await api.post('/admin/notifications/bulk-email-setting', {
        user_ids: selectedUserIds,
        group_ids: selectedGroupIds,
        email_notification: bulkEmailSetting
      });
      toast.success(response.data.message);
      setSelectedUserIds([]);
      setSelectedGroupIds([]);
    } catch (error) {
      toast.error('Failed to update email settings');
    } finally {
      setUpdatingBulk(false);
    }
  };

  const handleSendAdhocEmail = async () => {
    if (selectedUserIds.length === 0 && selectedGroupIds.length === 0) {
      toast.error('Please select at least one user or group');
      return;
    }
    setSendingAdhoc(true);
    try {
      const response = await api.post('/admin/notifications/adhoc-email', {
        user_ids: selectedUserIds,
        group_ids: selectedGroupIds
      });
      toast.success(response.data.message);
      setAdhocDialogOpen(false);
      setSelectedUserIds([]);
      setSelectedGroupIds([]);
    } catch (error) {
      toast.error('Failed to send emails');
    } finally {
      setSendingAdhoc(false);
    }
  };

  const handleSaveSetting = async (key, value) => {
    setSaving(true);
    try {
      await adminApi.updateSetting(key, value);
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Setting saved');
    } catch (error) {
      toast.error('Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSchedule = async () => {
    if (scheduleForm.user_ids.length === 0 && scheduleForm.group_ids.length === 0) {
      toast.error('Please select at least one user or group');
      return;
    }
    setCreatingSchedule(true);
    try {
      await api.post('/admin/digest/schedules', scheduleForm);
      toast.success('Digest schedule created');
      setScheduleDialogOpen(false);
      setScheduleForm({
        name: 'Daily Digest',
        user_ids: [],
        group_ids: [],
        send_hour: 8,
        send_minute: 0,
        exclude_weekends: true,
        active: true
      });
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setCreatingSchedule(false);
    }
  };

  const handleToggleSchedule = async (scheduleId) => {
    try {
      const res = await api.put(`/admin/digest/schedules/${scheduleId}/toggle`);
      toast.success(res.data.message);
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to toggle schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Delete this digest schedule?')) return;
    try {
      await api.delete(`/admin/digest/schedules/${scheduleId}`);
      toast.success('Schedule deleted');
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleRunScheduleNow = async (scheduleId) => {
    try {
      const res = await api.post(`/admin/digest/schedules/${scheduleId}/run`);
      toast.success(res.data.message);
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to run schedule');
    }
  };

  const formatTime = (hour, minute) => {
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:${String(minute).padStart(2, '0')} ${ampm} UTC`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="admin-settings-page">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            App Settings
          </h1>
          <p className="text-muted-foreground">Configure global application settings</p>
        </div>

        <div className="space-y-6">
          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#4A9CC7]" />
                General Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">App Name</p>
                  <p className="text-sm text-muted-foreground">Display name of the application</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={settings.app_name || ''}
                    onChange={(e) => setSettings({...settings, app_name: e.target.value})}
                    className="w-48 rounded-xl"
                    data-testid="app-name-input"
                  />
                  <Button 
                    size="icon" 
                    onClick={() => handleSaveSetting('app_name', settings.app_name)}
                    disabled={saving}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Maintenance Mode</p>
                  <p className="text-sm text-muted-foreground">Disable access for non-admin users</p>
                </div>
                <Switch
                  checked={settings.maintenance_mode || false}
                  onCheckedChange={(v) => handleSaveSetting('maintenance_mode', v)}
                  data-testid="maintenance-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Allow Registration</p>
                  <p className="text-sm text-muted-foreground">Allow new users to sign up</p>
                </div>
                <Switch
                  checked={settings.allow_registration !== false}
                  onCheckedChange={(v) => handleSaveSetting('allow_registration', v)}
                  data-testid="registration-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Limits Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Usage Limits</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Max Tasks (Free Tier)</p>
                  <p className="text-sm text-muted-foreground">Maximum tasks for free users</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={settings.max_tasks_free || 100}
                    onChange={(e) => setSettings({...settings, max_tasks_free: parseInt(e.target.value)})}
                    className="w-24 rounded-xl"
                    data-testid="max-tasks-free-input"
                  />
                  <Button 
                    size="icon" 
                    onClick={() => handleSaveSetting('max_tasks_free', settings.max_tasks_free)}
                    disabled={saving}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Max Tasks (Premium Tier)</p>
                  <p className="text-sm text-muted-foreground">Maximum tasks for premium users</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={settings.max_tasks_premium || 10000}
                    onChange={(e) => setSettings({...settings, max_tasks_premium: parseInt(e.target.value)})}
                    className="w-24 rounded-xl"
                    data-testid="max-tasks-premium-input"
                  />
                  <Button 
                    size="icon" 
                    onClick={() => handleSaveSetting('max_tasks_premium', settings.max_tasks_premium)}
                    disabled={saving}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Settings */}
          <Card>
            <CardHeader>
              <CardTitle>AI Features</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">AI Features Enabled</p>
                  <p className="text-sm text-muted-foreground">Enable AI-powered features globally</p>
                </div>
                <Switch
                  checked={settings.ai_enabled !== false}
                  onCheckedChange={(v) => handleSaveSetting('ai_enabled', v)}
                  data-testid="ai-enabled-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Default Focus Timer (minutes)</p>
                  <p className="text-sm text-muted-foreground">Default Pomodoro session duration</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={settings.focus_timer_default || 25}
                    onChange={(e) => setSettings({...settings, focus_timer_default: parseInt(e.target.value)})}
                    className="w-24 rounded-xl"
                    data-testid="focus-timer-input"
                  />
                  <Button 
                    size="icon" 
                    onClick={() => handleSaveSetting('focus_timer_default', settings.focus_timer_default)}
                    disabled={saving}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email Notification Controls */}
          <Card data-testid="notification-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#4A9CC7]" />
                Email Notification Controls
              </CardTitle>
              <CardDescription>
                Manage daily digest email settings for users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Bulk Update Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium flex items-center gap-2 text-foreground">
                  <Users className="w-4 h-4" />
                  Bulk Update Email Notification Setting
                </h4>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Users Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Select Users</label>
                    <ScrollArea className="h-40 border rounded-lg p-2">
                      {loadingUsers ? (
                        <div className="flex items-center justify-center h-full">
                          <Loader2 className="w-5 h-5 animate-spin" />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {users.map(user => (
                            <div key={user.user_id} className="flex items-center gap-2">
                              <Checkbox
                                id={`user-${user.user_id}`}
                                checked={selectedUserIds.includes(user.user_id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedUserIds(prev => [...prev, user.user_id]);
                                  } else {
                                    setSelectedUserIds(prev => prev.filter(id => id !== user.user_id));
                                  }
                                }}
                              />
                              <label htmlFor={`user-${user.user_id}`} className="text-sm cursor-pointer flex-1 text-foreground">
                                {user.name}
                                <span className="text-muted-foreground ml-1">({user.email})</span>
                                {user.email_notification && (
                                  <span className="ml-1 text-green-500 text-xs">Email ON</span>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </div>

                  {/* Groups Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Select Groups</label>
                    <ScrollArea className="h-40 border rounded-lg p-2">
                      {loadingUsers ? (
                        <div className="flex items-center justify-center h-full">
                          <Loader2 className="w-5 h-5 animate-spin" />
                        </div>
                      ) : groups.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No groups available</p>
                      ) : (
                        <div className="space-y-2">
                          {groups.map(group => (
                            <div key={group.group_id} className="flex items-center gap-2">
                              <Checkbox
                                id={`group-${group.group_id}`}
                                checked={selectedGroupIds.includes(group.group_id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedGroupIds(prev => [...prev, group.group_id]);
                                  } else {
                                    setSelectedGroupIds(prev => prev.filter(id => id !== group.group_id));
                                  }
                                }}
                              />
                              <label htmlFor={`group-${group.group_id}`} className="text-sm cursor-pointer flex-1 text-foreground">
                                {group.name}
                                <span className="text-muted-foreground ml-1">({group.member_count || 0} members)</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>

                <div className="flex items-center gap-4 pt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground">Set Email Notification to:</span>
                    <Select value={bulkEmailSetting ? 'yes' : 'no'} onValueChange={(v) => setBulkEmailSetting(v === 'yes')}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={handleBulkEmailUpdate}
                    disabled={updatingBulk || (selectedUserIds.length === 0 && selectedGroupIds.length === 0)}
                    className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                    data-testid="bulk-update-btn"
                  >
                    {updatingBulk ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
                    Update Setting
                  </Button>
                </div>
              </div>

              {/* Adhoc Email Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium flex items-center gap-2 text-foreground">
                  <Mail className="w-4 h-4" />
                  Send Adhoc Email Digest
                </h4>
                <p className="text-sm text-muted-foreground">
                  Send an immediate email digest to selected users/groups with workspace, project, and assigned member details.
                </p>
                <Button 
                  onClick={() => setAdhocDialogOpen(true)}
                  variant="outline"
                  className="border-[#4A9CC7] text-[#4A9CC7] hover:bg-[#4A9CC7]/10"
                  data-testid="send-adhoc-email-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Adhoc Email
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Scheduled Digest Management */}
          <Card data-testid="scheduled-digest-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarClock className="w-5 h-5 text-[#4A9CC7]" />
                    Scheduled Email Digests
                  </CardTitle>
                  <CardDescription>
                    Automate daily email digests for selected users or groups. Schedules run daily at the configured time (UTC), excluding weekends if configured.
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setScheduleDialogOpen(true)}
                  className="bg-[#4A9CC7] hover:bg-[#3d8ab5] shrink-0"
                  data-testid="create-schedule-btn"
                >
                  <Repeat className="w-4 h-4 mr-2" />
                  New Schedule
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingSchedules ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : schedules.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarClock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No scheduled digests yet</p>
                  <p className="text-sm mt-1">Create one to automate daily email digests</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {schedules.map(schedule => (
                    <div
                      key={schedule.schedule_id}
                      className={`border rounded-xl p-4 transition-colors ${schedule.active ? 'border-border' : 'border-border bg-muted/50 opacity-70'}`}
                      data-testid={`schedule-${schedule.schedule_id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-foreground">{schedule.name}</h4>
                            <Badge variant={schedule.active ? 'default' : 'secondary'} className={schedule.active ? 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30' : ''}>
                              {schedule.active ? 'Active' : 'Paused'}
                            </Badge>
                            {schedule.exclude_weekends && (
                              <Badge variant="outline" className="text-xs">Weekdays only</Badge>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {formatTime(schedule.send_hour, schedule.send_minute)}
                            </span>
                            {schedule.user_names?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                {schedule.user_names.length} user{schedule.user_names.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            {schedule.group_names?.length > 0 && (
                              <span>{schedule.group_names.join(', ')}</span>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-1 mt-2">
                            {(schedule.user_names || []).slice(0, 5).map((name, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{name}</Badge>
                            ))}
                            {(schedule.user_names || []).length > 5 && (
                              <Badge variant="outline" className="text-xs">+{schedule.user_names.length - 5} more</Badge>
                            )}
                          </div>

                          {schedule.last_run && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Last run: {new Date(schedule.last_run).toLocaleString()} - {schedule.last_run_result || 'No result'}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleRunScheduleNow(schedule.schedule_id)}
                            title="Run now"
                            data-testid={`run-schedule-${schedule.schedule_id}`}
                          >
                            <Send className="w-4 h-4 text-[#4A9CC7]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleToggleSchedule(schedule.schedule_id)}
                            title={schedule.active ? 'Pause' : 'Resume'}
                            data-testid={`toggle-schedule-${schedule.schedule_id}`}
                          >
                            {schedule.active ? <Pause className="w-4 h-4 text-amber-500" /> : <Play className="w-4 h-4 text-green-500" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                            title="Delete"
                            data-testid={`delete-schedule-${schedule.schedule_id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Adhoc Email Dialog */}
        <Dialog open={adhocDialogOpen} onOpenChange={setAdhocDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Send Adhoc Email Digest</DialogTitle>
              <DialogDescription>
                Select users and/or groups to send an immediate email digest with workspace, project, and member details.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Selected: {selectedUserIds.length} users, {selectedGroupIds.length} groups</label>
                <p className="text-sm text-muted-foreground">
                  Use the checkboxes in the Notification Controls section to select recipients.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAdhocDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendAdhocEmail}
                disabled={sendingAdhoc || (selectedUserIds.length === 0 && selectedGroupIds.length === 0)}
                className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
              >
                {sendingAdhoc ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Emails
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Schedule Dialog */}
        <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-[#4A9CC7]" />
                Create Scheduled Digest
              </DialogTitle>
              <DialogDescription>
                Set up a recurring daily email digest. Runs automatically at the configured time (UTC).
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-5 py-4">
              <div className="space-y-2">
                <Label>Schedule Name</Label>
                <Input
                  value={scheduleForm.name}
                  onChange={(e) => setScheduleForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Morning Digest"
                  data-testid="schedule-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Send Hour (UTC)</Label>
                  <Select value={String(scheduleForm.send_hour)} onValueChange={(v) => setScheduleForm(p => ({ ...p, send_hour: parseInt(v) }))}>
                    <SelectTrigger data-testid="schedule-hour-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>{`${String(i).padStart(2, '0')}:00 (${i % 12 || 12} ${i < 12 ? 'AM' : 'PM'})`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Send Minute</Label>
                  <Select value={String(scheduleForm.send_minute)} onValueChange={(v) => setScheduleForm(p => ({ ...p, send_minute: parseInt(v) }))}>
                    <SelectTrigger data-testid="schedule-minute-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">:00</SelectItem>
                      <SelectItem value="15">:15</SelectItem>
                      <SelectItem value="30">:30</SelectItem>
                      <SelectItem value="45">:45</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="exclude-weekends"
                  checked={scheduleForm.exclude_weekends}
                  onCheckedChange={(v) => setScheduleForm(p => ({ ...p, exclude_weekends: !!v }))}
                  data-testid="exclude-weekends-checkbox"
                />
                <label htmlFor="exclude-weekends" className="text-sm cursor-pointer text-foreground">
                  Exclude weekends (Saturday & Sunday)
                </label>
              </div>

              {/* User Selection */}
              <div className="space-y-2">
                <Label>Select Users</Label>
                <ScrollArea className="h-36 border rounded-lg p-2">
                  {loadingUsers ? (
                    <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin" /></div>
                  ) : (
                    <div className="space-y-2">
                      {users.map(user => (
                        <div key={user.user_id} className="flex items-center gap-2">
                          <Checkbox
                            id={`sched-user-${user.user_id}`}
                            checked={scheduleForm.user_ids.includes(user.user_id)}
                            onCheckedChange={(checked) => {
                              setScheduleForm(p => ({
                                ...p,
                                user_ids: checked
                                  ? [...p.user_ids, user.user_id]
                                  : p.user_ids.filter(id => id !== user.user_id)
                              }));
                            }}
                          />
                          <label htmlFor={`sched-user-${user.user_id}`} className="text-sm cursor-pointer text-foreground">
                            {user.name} <span className="text-muted-foreground">({user.email})</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Group Selection */}
              <div className="space-y-2">
                <Label>Select Groups</Label>
                <ScrollArea className="h-28 border rounded-lg p-2">
                  {groups.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">No groups available</p>
                  ) : (
                    <div className="space-y-2">
                      {groups.map(group => (
                        <div key={group.group_id} className="flex items-center gap-2">
                          <Checkbox
                            id={`sched-group-${group.group_id}`}
                            checked={scheduleForm.group_ids.includes(group.group_id)}
                            onCheckedChange={(checked) => {
                              setScheduleForm(p => ({
                                ...p,
                                group_ids: checked
                                  ? [...p.group_ids, group.group_id]
                                  : p.group_ids.filter(id => id !== group.group_id)
                              }));
                            }}
                          />
                          <label htmlFor={`sched-group-${group.group_id}`} className="text-sm cursor-pointer text-foreground">
                            {group.name} <span className="text-muted-foreground">({group.member_count || 0} members)</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button 
                onClick={handleCreateSchedule}
                disabled={creatingSchedule || (scheduleForm.user_ids.length === 0 && scheduleForm.group_ids.length === 0)}
                className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                data-testid="save-schedule-btn"
              >
                {creatingSchedule ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CalendarClock className="w-4 h-4 mr-2" />}
                Create Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
